<script setup>
// §8.6 TimesheetGrid (P1) — day/week block view with split-entry interaction.
// A vertical time axis with captured blocks positioned by start/end. Each block
// can be split (the canonical timesheet interaction) — hovering reveals a split
// handle that emits the minute offset. Blocks are neutral brand tints; the
// Client → Bucket label carries identity (D9), never colour. Times tabular (§5).
import { computed, ref } from 'vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
	startHour: { type: Number, default: 8 },
	endHour: { type: Number, default: 18 },
	// blocks: [{ id, start (min from midnight), end, client, bucket }]
	blocks: { type: Array, required: true },
})
const emit = defineEmits(['split', 'select'])

const hours = computed(() =>
{
	const out = []
	for (let h = props.startHour; h <= props.endHour; h++) out.push(h)
	return out
})
const span = computed(() => (props.endHour - props.startHour) * 60)
const top = (min) => ((min - props.startHour * 60) / span.value) * 100
const height = (b) => ((b.end - b.start) / span.value) * 100
const fmt = (min) =>
{
	const h = Math.floor(min / 60)
	const m = min % 60
	const ap = h < 12 ? 'am' : 'pm'
	const hh = h % 12 === 0 ? 12 : h % 12
	return `${hh}:${String(m).padStart(2, '0')}${ap}`
}

const hovered = ref(null)
</script>

<template>
	<div class="l-tsgrid">
		<div class="axis">
			<div v-for="h in hours" :key="h" class="hour">
				<span class="hlabel num">{{ ((h % 12) === 0 ? 12 : h % 12) }}{{ h < 12 ? 'am' : 'pm' }}</span>
			</div>
		</div>
		<div class="lane">
			<div v-for="h in hours" :key="h" class="gridline" aria-hidden="true" />
			<button v-for="b in blocks" :key="b.id" class="block"
				:style="{ top: top(b.start) + '%', height: height(b) + '%' }" @mouseenter="hovered = b.id"
				@mouseleave="hovered = null" @click="emit('select', b)">
				<div class="btime num">{{ fmt(b.start) }}–{{ fmt(b.end) }}</div>
				<div class="bpath">
					<span class="bclient">{{ b.client }}</span>
					<span class="barrow" aria-hidden="true">→</span>{{ b.bucket }}
				</div>
				<span v-if="hovered === b.id" class="split" role="button" tabindex="0" aria-label="Split entry"
					@click.stop="emit('split', { id: b.id, at: Math.round((b.start + b.end) / 2) })">
					<LIcon name="scissors" :size="12" /> Split
				</span>
			</button>
		</div>
	</div>
</template>

<style scoped>
.l-tsgrid
{
	display: grid;
	grid-template-columns: 54px 1fr;
	min-height: 420px;
}

.axis
{
	display: flex;
	flex-direction: column;
}

.hour
{
	flex: 1;
	position: relative;
}

.hlabel
{
	position: absolute;
	top: -7px;
	right: 10px;
	font-size: var(--text-micro);
	color: var(--text-3);
}

.lane
{
	position: relative;
	border-left: 1px solid var(--line-strong);
}

.gridline
{
	height: calc(100% / v-bind('hours.length'));
	border-bottom: 1px solid var(--line-soft);
}

.block
{
	position: absolute;
	left: 8px;
	right: 8px;
	background: var(--accent-fill);
	border-left: 3px solid var(--accent);
	border-radius: var(--r-sm);
	padding: 6px 10px;
	text-align: left;
	cursor: pointer;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.block:hover
{
	background: var(--accent-fill);
	filter: brightness(0.98);
}

.btime
{
	font-size: var(--text-micro);
	font-weight: 600;
	color: var(--accent-text);
}

.bpath
{
	font-size: var(--text-chip);
	color: var(--text);
}

.bclient
{
	font-weight: 600;
}

.barrow
{
	color: var(--decorative);
	margin: 0 4px;
}

.split
{
	position: absolute;
	top: 6px;
	right: 8px;
	display: inline-flex;
	align-items: center;
	gap: 4px;
	font-size: var(--text-micro);
	font-weight: 600;
	color: var(--interactive-text);
	background: var(--surface);
	border: 1px solid var(--line);
	border-radius: var(--r-sm);
	padding: 3px 7px;
	min-height: 22px;
}

.split:hover
{
	background: var(--interactive-tint);
}
</style>
